/**
 * "Günde bir kez, ayarlanan saatte koş" zamanlayıcısı (Sorgu Snapshot Faz 3).
 *
 * Faz 3'ün iki istemci bildirimi bunun üzerine kurulur: 22:45 ön-kontrolü
 * (DEV-2967) ve 08:00 sabah raporu (DEV-2968). GÖREV YÖNETİCİSİ'NE HİÇ
 * DOKUNULMAZ — bu modül `GorevYoneticisi`den ve `TaskHandler`lardan tamamen
 * bağımsızdır; yalnız zamanlayıcı yaşam döngüsü deseni (çift başlatma koruması,
 * saklanan handle) oradan örnek alınmıştır.
 *
 * FABRİKA, sınıf/singleton değil: iki zamanlayıcı birbirinden bağımsız duruma
 * (kendi handle'ı, kendi depolama anahtarı) sahip olmalı.
 *
 * ATEŞLEME KURALI: "bugün henüz ateşlemediysem VE günün hedef saati geçtiyse
 * ateşle." Sonuç olarak:
 *  - Uygulama saat geçtikten sonra açılırsa AÇILIŞTA ateşler (yakalama).
 *  - Uygulama gece yarısını açık geçirirse ertesi gün yeniden ateşler (her tikte
 *    tarih taze hesaplanır, ayrı bir gece yarısı mekanizması gerekmez).
 *  - Saat ileri alınırsa en fazla bir tik (60 sn) gecikmeyle ateşler.
 *  - Saat aynı gün içinde geri alınırsa gün damgası ikinci ateşlemeyi engeller.
 *
 * BİLİNEN AÇIK UÇ: sistem saati bir ÖNCEKİ güne çekilip sonra ileri alınırsa
 * aynı iş iki kez koşabilir. Elle saat kurcalama senaryosu olduğu ve iki işin de
 * yan etkisi yalnız bir bildirim olduğu için kabul edildi.
 */

export interface GunlukZamanlayiciSecenekleri {
    /**
     * localStorage anahtarı — "en son hangi gün ateşlendi" burada durur.
     * ZORUNLU: iki zamanlayıcı aynı anahtarı kullanırsa biri diğerini susturur.
     */
    depolamaAnahtari: string;
    /** Saat gelince çağrılır. Hata fırlatırsa yutulur (bkz. aşağıdaki not). */
    ateslendiginde: () => void | Promise<void>;
    /** Test için saat kaynağı. Varsayılan `new Date()`. */
    simdi?: () => Date;
    /** Test için tik aralığı. Varsayılan 60_000 ms. */
    tikMs?: number;
}

export interface GunlukZamanlayici {
    baslat(saat: number, dakika: number): void;
    durdur(): void;
    calisiyorMu(): boolean;
    /** Test/tanı: bugün ateşlendi mi (depolamadaki gün damgası). */
    sonAtesGunu(): string | null;
}

/** Yerel takvim günü, "YYYY-AA-GG". UTC'ye çevrilmez: karar yerel saatte verilir. */
function gunDamgasi(tarih: Date): string {
    const ay = String(tarih.getMonth() + 1).padStart(2, "0");
    const gun = String(tarih.getDate()).padStart(2, "0");
    return `${tarih.getFullYear()}-${ay}-${gun}`;
}

export function gunlukZamanlayiciOlustur(
    secenekler: GunlukZamanlayiciSecenekleri
): GunlukZamanlayici {
    let handle: any = null;
    const simdi = secenekler.simdi ?? (() => new Date());
    const tikMs = secenekler.tikMs ?? 60_000;

    function oku(): string | null {
        try {
            return localStorage.getItem(secenekler.depolamaAnahtari);
        } catch (e) {
            return null;
        }
    }

    function yaz(gun: string): void {
        try {
            localStorage.setItem(secenekler.depolamaAnahtari, gun);
        } catch (e) {
            // localStorage kapalıysa zamanlayıcı oturum içinde tekrar ateşleyebilir;
            // bildirim tekrarı, hiç bildirim gelmemesinden iyidir.
        }
    }

    function kontrolEtVeAtesle(saat: number, dakika: number): void {
        const an = simdi();
        const bugun = gunDamgasi(an);

        if (oku() === bugun) return;

        const hedef = new Date(an.getFullYear(), an.getMonth(), an.getDate(), saat, dakika, 0, 0);
        if (an.getTime() < hedef.getTime()) return;

        // GÜN DAMGASI ÖNCE YAZILIR. Sonra yazılsaydı ve geri çağrım patlasaydı
        // (ya da uzun sürüp bir sonraki tike taşsaydı) aynı bildirim gece boyunca
        // her 60 saniyede bir tekrarlanırdı.
        yaz(bugun);

        try {
            Promise.resolve(secenekler.ateslendiginde()).catch((e) => {
                console.warn("Günlük zamanlayıcı işi hata aldı:", e);
            });
        } catch (e) {
            console.warn("Günlük zamanlayıcı işi hata aldı:", e);
        }
    }

    return {
        baslat(saat: number, dakika: number): void {
            // Çift başlatma koruması: aynı desen GorevYoneticisi'nin taskInterval'ında.
            if (handle !== null) return;
            if (!Number.isInteger(saat) || saat < 0 || saat > 23) return;
            if (!Number.isInteger(dakika) || dakika < 0 || dakika > 59) return;

            // Yakalama: uygulama saat geçtikten sonra açıldıysa ilk tiki bekletme.
            kontrolEtVeAtesle(saat, dakika);
            handle = setInterval(() => kontrolEtVeAtesle(saat, dakika), tikMs);
        },
        durdur(): void {
            if (handle === null) return;
            clearInterval(handle);
            handle = null;
        },
        calisiyorMu(): boolean {
            return handle !== null;
        },
        sonAtesGunu(): string | null {
            return oku();
        }
    };
}
